import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import Countdown from "@/components/Countdown";
import { Reveal, MaskLine, SectionHead } from "@/components/Reveal";
import {
  IMAGES, STATS, ARCHITECTURE, PRINCIPLES, DOMAINS, FAQS, ROLES,
} from "@/data/content";

const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 240]);
  const scale = useTransform(scrollY, [0, 900], [1.05, 1.18]);

  return (
    <section className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden" data-testid="home-hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="A sprawling city skyline illuminated at night — the promise of urban India"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/55 to-obsidian/30" />
      </motion.div>
      <div className="relative z-10 mx-auto w-full max-w-[1600px] px-5 pb-14 pt-32 md:px-10 md:pb-24 md:pt-40">
        <MaskLine delay={0.15}>
          <span className="text-xs uppercase tracking-[0.2em] sm:tracking-[0.35em] text-gold md:text-sm">
            Smart Nagar Nigam — Governance That Reaches Every Ward
          </span>
        </MaskLine>
        <h1 className="mt-6 font-display font-extrabold uppercase leading-[0.95] tracking-tight text-ivory">
          <MaskLine delay={0.3} className="text-[13vw] md:text-[9vw] lg:text-[7.5vw]">Civic Intelligence.</MaskLine>
          <MaskLine delay={0.45} className="text-[13vw] md:text-[9vw] lg:text-[7.5vw]">Better Cities.</MaskLine>
          <MaskLine delay={0.6} className="text-[13vw] md:text-[9vw] lg:text-[7.5vw]">
            <span className="text-outline">Empowered Citizens.</span>
          </MaskLine>
        </h1>
        <div className="mt-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <MaskLine delay={0.8}>
            <p className="max-w-xl text-base leading-relaxed text-white/70 md:text-lg">
              A permanent, non-partisan institution transforming ward-level governance
              across India — through data-driven accountability, citizen participation,
              public leadership, and technology-enabled urban transformation.
              Delhi is Edition One.
            </p>
          </MaskLine>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-wrap gap-4"
          >
            <Link
              to="/event"
              data-testid="hero-conclave-cta"
              className="inline-flex w-full items-center justify-center gap-3 bg-gold px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-obsidian transition-colors duration-300 hover:bg-gold-light sm:w-auto"
            >
              The Conclave <ArrowRight size={16} />
            </Link>
            <Link
              to="/register"
              data-testid="hero-register-cta"
              className="inline-flex w-full items-center justify-center gap-3 border border-white/25 px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-ivory transition-colors duration-300 hover:border-gold hover:text-gold sm:w-auto"
            >
              Register <ArrowUpRight size={16} />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Ribbon = ({ items, dark = false }) => (
  <div className={`border-y py-5 ${dark ? "border-white/10 bg-coal" : "border-gold/30 bg-gold"}`} data-testid="editorial-ribbon">
    <Marquee speed={28} gradient={false} pauseOnHover>
      {items.map((t, i) => (
        <span key={i} className={`mx-8 font-display text-sm font-bold uppercase tracking-[0.3em] ${dark ? "text-white/50" : "text-obsidian"}`}>
          {t} <span className={`ml-8 ${dark ? "text-gold/60" : "text-obsidian/40"}`}>·</span>
        </span>
      ))}
    </Marquee>
  </div>
);

const Chapter = ({ index, overline, title, children, id, light = false }) => (
  <section id={id} className={`border-t border-white/10 ${light ? "section-light" : ""}`} data-testid={`chapter-${index}`}>
    <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
      <SectionHead index={index} overline={overline} title={title} />
      {children}
    </div>
  </section>
);

const Home = () => (
  <>
    <SEO
      title={null}
      path="/"
      description="Smart Nagar Nigam — a non-partisan civic intelligence institution transforming ward-level governance across India through data-driven accountability, citizen participation, public leadership, and technology-enabled urban transformation."
    />
    <Hero />
    <Ribbon
      items={[
        "From Wards 2 the World · 30 October 2026",
        "NDMC Convention Centre · New Delhi · 2 PM – 6 PM",
        "Delhi Governance Roundtable Conclave",
        "Building the World's Best Capital, One Ward at a Time",
        "250 Wards · 20 Million Citizens · One Covenant",
      ]}
    />

    <Chapter index="01" overline="The Institution" title="Governance, Measured at the Last Mile.">
      <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <p className="text-base leading-relaxed text-white/70 md:text-lg">
            Smart Nagar Nigam is a permanent, non-partisan civic intelligence
            institution — built on six years of urban governance research —
            transforming ward-level governance across India through data-driven
            accountability, citizen participation, public leadership, and
            technology-enabled urban transformation. The last mile of governance —
            the ward — has remained unmeasured, unaccountable and invisible to the very
            citizens it serves. Smart Nagar Nigam was built to close that gap.
          </p>
          <div className="mt-10 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2">
            {PRINCIPLES.map((p) => (
              <div key={p.name} className="bg-obsidian p-6">
                <h3 className="font-display text-lg font-bold uppercase tracking-wide text-gold">{p.name}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/60">{p.means}</p>
              </div>
            ))}
          </div>
          <Link to="/about" data-testid="home-about-link" className="mt-10 inline-flex items-center gap-3 font-display text-sm font-bold uppercase tracking-[0.15em] text-gold transition-colors duration-300 hover:text-gold-light">
            About Smart Nagar Nigam <ArrowRight size={16} />
          </Link>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="relative h-full min-h-[420px] overflow-hidden border border-white/10">
            <img src={IMAGES.city} alt="Singapore skyline at dusk — the global benchmark for governed cities" className="h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent" />
            <p className="absolute bottom-6 left-6 max-w-xs font-display text-xl font-bold leading-snug text-ivory">
              "To establish Delhi as a global benchmark for urban governance."
            </p>
          </div>
        </Reveal>
      </div>
    </Chapter>

    <section className="section-light border-t border-white/10" data-testid="stats-band">
      <div className="mx-auto grid max-w-[1600px] grid-cols-2 gap-px bg-white/10 md:grid-cols-3 lg:grid-cols-6">
        {STATS.map((s, i) => (
          <div key={s.label} className="bg-coal p-8 md:p-10">
            <Reveal delay={i * 0.05}>
              <p className="font-display text-4xl font-extrabold tracking-tight text-gold md:text-5xl">{s.value}</p>
              <p className="mt-3 text-xs uppercase tracking-[0.2em] text-white/50">{s.label}</p>
            </Reveal>
          </div>
        ))}
      </div>
    </section>

    <Chapter index="02" overline="India's Moment — Delhi's Responsibility" title="The World Is Watching India. India Is Watching Delhi.">
      <div className="grid gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-7">
          <p className="text-base leading-relaxed text-white/70 md:text-lg">
            India stands at the most consequential juncture in its post-Independence history.
            The world's most populous democracy and fastest-growing major economy is a global
            power in the making — G20 presidency, leadership of the Global South, Digital
            Public Infrastructure, and the ambition of Viksit Bharat 2047. Urban India is the
            engine of that ambition: by 2047, more than half of India will be urban, and
            cities already generate over 60 percent of GDP.
          </p>
          <p className="mt-6 text-base leading-relaxed text-white/70 md:text-lg">
            Delhi — with 20 million citizens, 250 wards and a population approaching 32 million
            by 2047 — carries a governance responsibility no city in history has borne at this
            scale. The Master Plan for Delhi 2047 is a statement of civilisational ambition.
            The ward is where that ambition either becomes lived reality or remains archived
            aspiration.
          </p>
          <Link to="/vision" data-testid="home-vision-link" className="mt-10 inline-flex items-center gap-3 font-display text-sm font-bold uppercase tracking-[0.15em] text-gold transition-colors duration-300 hover:text-gold-light">
            Explore Vision 2047 <ArrowRight size={16} />
          </Link>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-5">
          <div className="border border-gold/30 bg-coal p-8 md:p-10">
            <p className="text-xs uppercase tracking-[0.25em] text-gold">MPD-2047 — Highlights</p>
            <ul className="mt-6 space-y-4 text-sm leading-relaxed text-white/70">
              {[
                "30–40 lakh new housing units",
                "24-hour city zones & air taxis",
                "Inland waterways on the Yamuna",
                "200-km cycling & park connector network",
                "1,700 hectares of Yamuna floodplain restoration",
                "Dwarka as an international investment hub",
                "GIS & digital-platform-driven urban governance",
              ].map((t) => (
                <li key={t} className="flex gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 bg-gold" aria-hidden="true" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </Chapter>

    <Chapter index="03" overline="The Constitutional Mandate" title="Completing the Unfinished Promise of the 74th Amendment." light>
      <div className="grid gap-12 lg:grid-cols-2">
        <Reveal>
          <p className="text-base leading-relaxed text-white/70 md:text-lg">
            The 74th Constitutional Amendment Act of 1992 — in force since 1st June 1993 —
            gave Urban Local Bodies constitutional status for the first time in India's
            history, through Part IX-A (Articles 243-P to 243-ZG) and the 12th Schedule.
            Three decades later, its promise — functioning ward committees, accountable
            councillors, citizens as partners — remains largely aspirational.
          </p>
          <p className="mt-6 border-l-2 border-gold pl-6 font-display text-xl font-bold leading-snug text-ivory md:text-2xl">
            "In seventy-five years of Independence, the 74th Amendment's promise of ward-level
            democratic accountability has never been operationalised at scale. Smart Nagar
            Nigam exists to complete that unfinished project."
          </p>
          <Link to="/mandate" data-testid="home-mandate-link" className="mt-10 inline-flex items-center gap-3 font-display text-sm font-bold uppercase tracking-[0.15em] text-gold transition-colors duration-300 hover:text-gold-light">
            The Constitutional Mandate <ArrowRight size={16} />
          </Link>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="grid grid-cols-2 gap-px border border-white/10 bg-white/10">
            {["243-Q · Composition", "243-S · Ward Committees", "243-W · Powers", "243-Y · Finance", "243-Z · Audit", "243-ZD/ZE · Planning"].map((a) => (
              <div key={a} className="bg-obsidian p-6 md:p-8">
                <p className="font-display text-base font-bold text-ivory md:text-lg">{a}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </Chapter>

    <section className="relative overflow-hidden border-t border-white/10" data-testid="home-event-spotlight">
      <div className="absolute inset-0">
        <img src={IMAGES.roundtable} alt="A cross-sector governance roundtable in session" className="h-full w-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-obsidian/85" />
      </div>
      <div className="relative z-10 mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="04" overline="The Inaugural Conclave" title="From Wards 2 the World." />
        <Reveal>
          <Countdown className="mb-14" />
        </Reveal>
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <p className="font-display text-xl font-bold leading-snug text-ivory md:text-2xl">
              Delhi Governance Roundtable Conclave — Building the World's Best Capital,
              One Ward at a Time.
            </p>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/70 md:text-lg">
              On 30 October 2026, fifty to fifty-five of Delhi's most consequential
              cross-sector voices gather at the NDMC Convention Centre, New Delhi, to launch
              the Good Governance Book, unveil Citizen Councillor Connect (C3), sign the
              Delhi Ward Charter — and trigger a 150+ platform national media wave. This is
              not a conference. It is a covenant.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/event" data-testid="home-event-cta" className="inline-flex items-center gap-3 bg-gold px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-obsidian transition-colors duration-300 hover:bg-gold-light">
                The Full Event <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <img
              src={IMAGES.posterMain}
              alt="From Wards to the World — Delhi Governance Roundtable Conclave: 30 October 2026, 2 PM – 6 PM, NDMC Convention Centre, New Delhi, followed by an exclusive dinner buffet"
              className="w-full border border-white/15 object-cover"
              loading="lazy"
            />
            <dl className="mt-6 border border-white/15 bg-black/50 backdrop-blur-md">
              {[
                ["Date & Time", "30 October 2026 · 2:00 PM – 6:00 PM · Followed by an Exclusive Dinner Buffet"],
                ["Venue", "NDMC Convention Centre, Jai Singh Road, New Delhi"],
                ["Theme", "Transforming Delhi Through Data, Leadership & Citizen Partnership"],
                ["Invitees", "50–55 Distinguished Guests · Exclusive Invitee-Based Roundtable"],
                ["Character", "Non-Partisan · High-Trust · Closed-Format · Inaugural Edition"],
              ].map(([k, v]) => (
                <div key={k} className="border-b border-white/10 p-5 last:border-b-0">
                  <dt className="text-[11px] uppercase tracking-[0.25em] text-gold">{k}</dt>
                  <dd className="mt-2 text-sm leading-relaxed text-white/75">{v}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
      </div>
    </section>

    <Chapter index="05" overline="The Platform Architecture" title="Six Initiatives. One Ecosystem of Accountability.">
      <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-3">
        {ARCHITECTURE.map((a, i) => (
          <Reveal key={a.title} delay={i * 0.06} className="bg-obsidian">
            <Link to="/initiatives" data-testid={`home-initiative-${i}`} className="group block h-full p-8 md:p-10">
              <span className="font-display text-sm font-bold tracking-[0.3em] text-gold">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-4 font-display text-xl font-bold leading-snug text-ivory transition-colors duration-300 group-hover:text-gold md:text-2xl">
                {a.title}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-white/60">{a.text}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/40 transition-colors duration-300 group-hover:text-gold">
                Explore <ArrowUpRight size={14} />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </Chapter>

    <Ribbon
      dark
      items={DOMAINS.filter((_, i) => i % 2 === 0).map((d) => d.d)}
    />

    <Chapter index="06" overline="Answers" title="Questions, Answered in Full." light>
      <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-2">
        {FAQS.slice(0, 4).map((f, i) => (
          <Reveal key={f.q} delay={i * 0.05} className="bg-obsidian p-8 md:p-10">
            <h3 className="font-display text-lg font-bold leading-snug text-ivory md:text-xl">{f.q}</h3>
            <p className="mt-4 line-clamp-3 text-sm leading-relaxed text-white/60">{f.a}</p>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <Link to="/faqs" data-testid="home-faqs-link" className="mt-10 inline-flex items-center gap-3 font-display text-sm font-bold uppercase tracking-[0.15em] text-gold transition-colors duration-300 hover:text-gold-light">
          All FAQs <ArrowRight size={16} />
        </Link>
      </Reveal>
    </Chapter>

    <section className="border-t border-gold/30 bg-gold" data-testid="home-register-band">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-28">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-obsidian/70">Join the Movement</p>
          <h2 className="mt-4 max-w-4xl font-display text-4xl font-extrabold uppercase leading-[0.98] tracking-tight text-obsidian sm:text-5xl lg:text-6xl">
            Every Seat in This Room Shapes Delhi's Next Decade.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-2">
            {ROLES.map((r) => (
              <span key={r} className="border border-obsidian/30 px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] text-obsidian">
                {r}
              </span>
            ))}
          </div>
          <Link to="/register" data-testid="home-register-band-cta" className="mt-10 inline-flex items-center gap-3 bg-obsidian px-10 py-5 font-display text-sm font-bold uppercase tracking-[0.15em] text-gold transition-colors duration-300 hover:bg-coal">
            Register Your Place <ArrowRight size={16} />
          </Link>
        </Reveal>
      </div>
    </section>
  </>
);

export default Home;
